import axios from 'axios';
import { logout } from 'store/auth';
import { store } from 'store/index';

// API base URL resolution:
//   * Dev (`yarn start` → NODE_ENV=development): use empty baseURL so
//     axios fires requests at the current origin (http://localhost:3000),
//     which CRA's dev server proxies to the real backend via
//     src/setupProxy.js. Browser sees same-origin → cookies just work.
//   * Production: use REACT_APP_API_URL if set, otherwise the current
//     window origin so a single image can serve multiple hostnames
//     (e.g. dev.app.orphix.ai, app.orphix.ai). Set REACT_APP_API_URL at
//     build time only when the panel is served from a different origin
//     than the API.
const getBaseURL = () => {
  const currentLocation = window.location;
  return `${currentLocation.protocol}//${currentLocation.hostname}`;
};

const baseURL =
  process.env.NODE_ENV === 'development'
    ? ''
    : (process.env.REACT_APP_API_URL || getBaseURL());

// Cookie-session auth: the backend's SuperAdminLoginView attaches an
// `orphix_sessionid` cookie via Django auth.login(); DRF's
// SessionAuthentication (first in DEFAULT_AUTHENTICATION_CLASSES) reads
// it back on every /api/super/* call. Two pieces are required:
//   1. withCredentials so the browser sends the cookie on every XHR.
//   2. xsrfCookieName / xsrfHeaderName so axios auto-reads Django's
//      `orphix_csrftoken` cookie and attaches it as X-CSRFToken on
//      unsafe methods (POST/PUT/PATCH/DELETE) — matches the names set
//      in settings.py (CSRF_COOKIE_NAME / CSRF_HEADER_NAME default).
const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  xsrfCookieName: 'orphix_csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json'
  }
});

// On 401 the session is gone (cookie expired / cleared / never set).
// There is no refresh-token flow in cookie mode — drop the redux auth
// state and let GuestGuard kick the user back to /login.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      store.dispatch(logout());
    }
    return Promise.reject(error);
  }
);

// One-shot bootstrap: ping the CSRF endpoint so Django sets the
// `orphix_csrftoken` cookie before any login POST. Called eagerly at
// module load so it has run by the time the user submits the form.
export const bootstrapCsrf = () => axiosInstance.get('/api/account/csrf/').catch(() => {
  // Non-fatal: login can still succeed because /api/account/super-admin/login/
  // is csrf_exempt. Unsafe writes after login will fail loudly if the cookie
  // never made it through CORS, so we want a clear error then, not here.
});
bootstrapCsrf();

export default axiosInstance;

// Kept for backwards compatibility with any code still importing it
// (e.g. older refresh-token wrappers). Same config as the main instance.
export const axiosInstanceForRefreshToken = axios.create({
  baseURL,
  withCredentials: true,
  xsrfCookieName: 'orphix_csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json'
  }
});
